/**
 * Green card grid showing the five organizational-level GHG emission metrics.
 * Used by both CompanyGHGPage and GHGOverviewPage.
 */

import React from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';

export interface OrgEmissionItem {
  label: string;
  value: string;
  unit: string;
  subtitle: string;
}

interface Props {
  items: OrgEmissionItem[];
}

const OrganizationalEmissionsCards: React.FC<Props> = ({ items }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
      Organizational Level Emissions
    </Typography>
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={2.4} key={item.label} sx={{ display: 'flex' }}>
          <Card
            elevation={3}
            sx={{
              background: '#90BF45',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                background: '#90BF45',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  opacity: 0.85,
                  display: 'block',
                  mb: 0.5,
                  color: 'inherit',
                  minHeight: '2.4rem',
                  lineHeight: 1.2,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  mb: 0.25,
                  color: 'inherit',
                  wordBreak: 'break-word',
                }}
              >
                {item.value}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 500, color: 'inherit' }}>
                {item.unit}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.75,
                  fontSize: '0.72rem',
                  mt: 'auto',
                  pt: 0.5,
                  display: 'block',
                  color: 'inherit',
                  minHeight: '1.2rem',
                }}
              >
                {item.subtitle}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

export default OrganizationalEmissionsCards;
